
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { TestResponse, AIFeedback } from "@/types/test-response";
import { LoadingState } from "./responses/states/LoadingState";
import { ErrorState } from "./responses/states/ErrorState";
import { useTestScores } from "@/hooks/useTestScores";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TestResponseHeader } from "./responses/TestResponseHeader";
import { useIsMobile } from "@/hooks/use-mobile";
import { MonitoringEvent, MonitoringEventType } from "@/types/monitoring";
import { CandidateDetailsCard } from "./responses/CandidateDetailsCard";
import { AdjacentResponseNavigation } from "./responses/AdjacentResponseNavigation";
import { ResponsesTab } from "./responses/tabs/ResponsesTab";
import { MonitoringTab } from "./responses/tabs/MonitoringTab";
import { ProfileTab } from "./responses/tabs/ProfileTab";
import { CVTab } from "./responses/tabs/CVTab";
import { CalendlyTab } from "./responses/tabs/CalendlyTab";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { SortOption } from "./responses-table/SortAndSearchBar";
import { sortResponsesByAIScore, transformTestResponse } from "@/utils/response-utils";
import { RecruitmentStatus } from "@/types/test-response";

export function TestResponseView() {
  const { responseId } = useParams();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Get state from URL params to maintain consistency with the table view
  const sortOption = (searchParams.get("sort") as SortOption) || "recent";
  const searchQuery = searchParams.get("search") || "";
  const activeFilters = searchParams.get("filters") 
    ? (searchParams.get("filters")!.split(",") as RecruitmentStatus[])
    : [];
  
  // Track if we need to force refresh
  const [needsRefresh, setNeedsRefresh] = useState(false);

  const { data: response, isLoading, refetch } = useQuery({
    queryKey: ["test-response", responseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("test_responses")
        .select(`
          *,
          candidate:profiles(
            full_name, 
            email,
            location,
            job_title,
            industry,
            avatar_url,
            organization,
            phone_number,
            cv_url,
            calendly_url
          ),
          test:tests(
            id,
            title,
            job_title,
            location,
            work_presence,
            work_arrangement,
            seniority,
            years_of_experience,
            questions!fk_test(
              id,
              created_at,
              test_id,
              question_type,
              content,
              marks,
              order,
              time_limit,
              skill,
              difficulty,
              markscheme
            )
          ),
          question_responses(*)
        `)
        .eq("id", responseId)
        .single();

      if (error) throw error;
      
      const transformedData = {
        ...data,
        question_responses: data.question_responses.map((qr: any) => ({
          ...qr,
          ai_feedback: qr.ai_feedback ? {
            score: qr.ai_feedback.score,
            overall_feedback: qr.ai_feedback.overall_feedback,
            strengths: qr.ai_feedback.strengths || [],
            improvements: qr.ai_feedback.improvements || [],
            criteria_scores: qr.ai_feedback.criteria_scores || {},
            review_points: qr.ai_feedback.review_points || {}
          } as AIFeedback : null
        }))
      } as TestResponse;

      return transformedData;
    },
  });
  
  // Listen for score updates
  useEffect(() => {
    const handleScoreUpdate = (event: CustomEvent) => {
      console.log("TestResponseView detected score update:", event.detail);
      if (responseId && event.detail.testResponseId === responseId) {
        setNeedsRefresh(true);
        queryClient.invalidateQueries({
          queryKey: ["test-response", responseId]
        });
        refetch();
      }
    };

    window.addEventListener('scoreUpdated', handleScoreUpdate as EventListener);
    
    return () => {
      window.removeEventListener('scoreUpdated', handleScoreUpdate as EventListener);
    };
  }, [responseId, queryClient, refetch]);
  
  // If needsRefresh is true, force a refetch and reset
  useEffect(() => {
    if (needsRefresh) {
      refetch();
      setNeedsRefresh(false);
    }
  }, [needsRefresh, refetch]);

  // Fetch adjacent (previous and next) test responses with respect to current sorting/filtering
  const { data: adjacentResponses } = useQuery({
    queryKey: ["adjacent-responses", responseId, response?.test_id, sortOption, searchQuery, activeFilters],
    queryFn: async () => {
      if (!responseId || !response?.test_id) return { previous: null, next: null };
      
      // Build the base query with the same structure as the main responses table
      let query = supabase
        .from("test_responses")
        .select(`
          id, status, started_at, completed_at, score, scores_confirmed, total_ai_score,
          recruitment_status, recruitment_notes, created_at, integrity_score,
          test_id, candidate_id, recruitment_updated_at, interview_invited_at, interview_invited_by,
          candidate:profiles(full_name, email, avatar_url, phone_number, cv_url),
          question_responses(
            id, question_id, response_content, score, 
            ai_feedback, evaluation_status, transcription_status, audio_url
          ),
          test:tests(id, title, questions!fk_test(id, marks, order, skill, difficulty, content, question_type, time_limit, markscheme)) 
        `)
        .eq("test_id", response.test_id);

      // Apply search filter if present (with correct syntax)
      if (searchQuery.trim()) {
        const sqLower = searchQuery.trim().toLowerCase();
        query = query.or(
          `candidate.full_name.ilike.%${sqLower}%,candidate.email.ilike.%${sqLower}%`
        );
      }

      // Apply recruitment status filters if present
      if (activeFilters.length > 0) {
        query = query.in("recruitment_status", activeFilters);
      }

      // Apply sorting based on the sort option (server-side for database columns)
      if (sortOption === "oldest") {
        query = query.order("created_at", { ascending: true });
      } else if (sortOption === "highestIntegrity") {
        query = query.order("integrity_score", { ascending: false, nullsFirst: false });
      } else if (sortOption === "recent") {
        query = query.order("created_at", { ascending: false });
      }
      // Note: AI score sorting will be handled client-side

      const { data: allResponses, error } = await query;
        
      if (error || !allResponses) throw error;

      // Transform responses using the same utility function as the main table
      const transformedResponses = allResponses.map((resp: any) => 
        transformTestResponse(resp, response.test_id, response.test.title)
      );

      let sortedResponses = transformedResponses;

      // Handle AI score sorting client-side (using the same logic as usePaginatedTestResponses)
      if (sortOption === "highestScore" || sortOption === "lowestScore") {
        sortedResponses = sortResponsesByAIScore(
          transformedResponses, 
          sortOption === "lowestScore"
        );
      }
      
      // Find the current response index in the sorted/filtered list
      const currentIndex = sortedResponses.findIndex(r => r.id === responseId);
      if (currentIndex === -1) return { previous: null, next: null };
      
      // Get previous and next responses based on the sorted order
      const previousResponse = currentIndex > 0 
        ? sortedResponses[currentIndex - 1] 
        : null;
        
      const nextResponse = currentIndex < sortedResponses.length - 1 
        ? sortedResponses[currentIndex + 1] 
        : null;
        
      return { 
        previous: previousResponse, 
        next: nextResponse
      };
    },
    enabled: !!responseId && !!response?.test_id,
  });

  const { data: monitoringEvents } = useQuery({
    queryKey: ["monitoring-events", responseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("test_monitoring_events")
        .select("*")
        .eq("test_response_id", responseId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const validEventTypes: MonitoringEventType[] = [
        'tab_switch',
        'window_blur',
        'timing_anomaly',
        'ai_content',
        'content_similarity',
        'device_location',
        'clipboard_action',
        'webcam_capture'
      ];

      return data.filter(event => 
        validEventTypes.includes(event.event_type as MonitoringEventType)
      ) as MonitoringEvent[];
    },
    enabled: !!responseId,
  });

  const {
    scores,
    isEditing,
    setIsEditing,
    handleScoreChange,
    handleAcceptAIScores: baseHandleAcceptAIScores,
    handleConfirmScores: baseHandleConfirmScores
  } = useTestScores(response);

  // Wrap the score handlers to invalidate queries after completion
  const handleAcceptAIScores = async () => {
    await baseHandleAcceptAIScores();
    // Invalidate queries to refetch data
    await queryClient.invalidateQueries({
      queryKey: ["test-response", responseId]
    });
    
    // Invalidate the test responses list if applicable
    if (response?.test_id) {
      await queryClient.invalidateQueries({
        queryKey: ["test-responses", response.test_id]
      });
    }
    
    // Force refetch this specific response
    refetch();
  };

  const handleConfirmScores = async () => {
    await baseHandleConfirmScores();
    // Invalidate queries to refetch data
    await queryClient.invalidateQueries({
      queryKey: ["test-response", responseId]
    });
    
    // Invalidate the test responses list if applicable
    if (response?.test_id) {
      await queryClient.invalidateQueries({
        queryKey: ["test-responses", response.test_id]
      });
    }
    
    // Force refetch this specific response
    refetch();
  };

  const handleResponseNavigation = (newResponseId: string) => {
    // Preserve current URL parameters when navigating to adjacent response
    const currentParams = searchParams.toString();
    const url = `/test-response/${newResponseId}${currentParams ? `?${currentParams}` : ''}`;
    navigate(url);
  };


  if (isLoading) {
    return <LoadingState />;
  }

  if (!response) {
    return <ErrorState />;
  }

  // Add monitoring events to the response so components can use it
  const responseWithEvents = {
    ...response,
    events: monitoringEvents
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-6xl mx-auto py-8 px-4 sm:px-6 space-y-8">
        {/* Put the back button outside the card */}
        <TestResponseHeader 
          testId={response.test.id} 
          candidateName={response.candidate.full_name}
          testResponse={response}
        />
        
        {/* Enhanced tab structure */}
        <Tabs defaultValue="responses" className="w-full">
          <TabsList className={`w-full md:w-auto ${isMobile ? 'grid grid-cols-5' : 'inline-flex'} text-foreground bg-background border-b rounded-none mb-6`}>
            <TabsTrigger 
              value="responses" 
              className={`
                ${isMobile ? 'flex-1 text-xs' : 'px-6'} 
                py-3 font-medium 
                rounded-none
                data-[state=active]:bg-transparent 
                data-[state=active]:text-foreground 
                data-[state=active]:shadow-none
                data-[state=active]:border-b-2
                data-[state=active]:border-foreground
                transition-colors
              `}
            >
              Responses
            </TabsTrigger>
            <TabsTrigger 
              value="monitoring" 
              className={`
                ${isMobile ? 'flex-1 text-xs' : 'px-6'} 
                py-3 font-medium 
                rounded-none
                data-[state=active]:bg-transparent 
                data-[state=active]:text-foreground 
                data-[state=active]:shadow-none
                data-[state=active]:border-b-2
                data-[state=active]:border-foreground
                transition-colors
              `}
            >
              Monitoring
            </TabsTrigger>
            <TabsTrigger 
              value="profile" 
              className={`
                ${isMobile ? 'flex-1 text-xs' : 'px-6'} 
                py-3 font-medium 
                rounded-none
                data-[state=active]:bg-transparent 
                data-[state=active]:text-foreground 
                data-[state=active]:shadow-none
                data-[state=active]:border-b-2
                data-[state=active]:border-foreground
                transition-colors
              `}
            >
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="cv" 
              className={`
                ${isMobile ? 'flex-1 text-xs' : 'px-6'} 
                py-3 font-medium 
                rounded-none
                data-[state=active]:bg-transparent 
                data-[state=active]:text-foreground 
                data-[state=active]:shadow-none
                data-[state=active]:border-b-2
                data-[state=active]:border-foreground
                transition-colors
              `}
            >
              CV
            </TabsTrigger>
            <TabsTrigger 
              value="interview" 
              className={`
                ${isMobile ? 'flex-1 text-xs' : 'px-6'} 
                py-3 font-medium 
                rounded-none
                data-[state=active]:bg-transparent 
                data-[state=active]:text-foreground 
                data-[state=active]:shadow-none
                data-[state=active]:border-b-2
                data-[state=active]:border-foreground
                transition-colors
              `}
            >
              Interview
            </TabsTrigger>
          </TabsList>

          {/* Tabs Content */}
          <TabsContent value="responses" className="mt-2 animate-in fade-in-50">
            <ResponsesTab
              response={responseWithEvents}
              isLoading={isLoading}
              scores={scores}
              isEditing={isEditing}
              onScoreChange={handleScoreChange}
              setIsEditing={setIsEditing}
              handleConfirmScores={handleConfirmScores}
              handleAcceptAIScores={handleAcceptAIScores}
              monitoringEvents={monitoringEvents || []}
            />
          </TabsContent>

          <TabsContent value="monitoring" className="mt-2 animate-in fade-in-50">
            <MonitoringTab testResponse={responseWithEvents} />
          </TabsContent>

          <TabsContent value="profile" className="mt-2 animate-in fade-in-50">
            <ProfileTab candidate={response.candidate} />
          </TabsContent>

          <TabsContent value="cv" className="mt-2 animate-in fade-in-50">
            <CVTab candidate={response.candidate} />
          </TabsContent>

          <TabsContent value="interview" className="mt-2 animate-in fade-in-50">
            <CalendlyTab 
              candidate={response.candidate} 
              testResponseId={response.id} 
              interviewInvitedAt={response.interview_invited_at}
            />
          </TabsContent>
        </Tabs>
        
        {/* Navigation between responses now appears at the end of the page */}
        <div className="mt-8">
          <h4 className="text-sm font-medium text-muted-foreground mb-4">Navigate Test Responses</h4>
          <AdjacentResponseNavigation
            previousResponse={adjacentResponses?.previous}
            nextResponse={adjacentResponses?.next}
            onNavigate={handleResponseNavigation}
          />
        </div>
      </div>
    </div>
  );
}
